import { useEffect, useRef, useState } from "react";
import Popup from "../../ReusableFunctions/Popup";
import MarkerIcon from "../../assets/marker.png";
import L from 'leaflet';
import roadMapView from '../../assets/roadMapView.png';
import sateliteView from '../../assets/sateliteView.svg';
import { MAP_URLS } from "../../appConstants";
import '../../AdminComponents/Locations/Location.css';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import moment from 'moment';
import fullScreenIcon from '../../assets/fullScreenIcon.png';
import collapseIcon from '../../assets/collapse.png';
import LoaderComponent from "../../ReusableComponents/LoaderComponent";
import reSizeIcon from '../../assets/reSize.svg';
const ViewRequestsOnMap = ({
    mapData,
    isExpandable,
    isLoading,
    height
}) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const baseLayerRef = useRef(null);
    const clusterRef = useRef(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [activeBaseLayer, setActiveBaseLayer] = useState("terrain");
    const [center] = useState({ lat: 19.993982, lng: 73.790416 });

    // Initialize marker icon
    const markerIcon = L.icon({
        iconUrl: MarkerIcon,
        iconSize: [24, 32],
    });

    // Initialize the map
    useEffect(() => {
        if (!mapInstanceRef.current && mapRef.current) {
            const map = L.map(mapRef.current, {
                center: [center.lat, center.lng],
                zoom: 6,
                maxZoom: 25
            });

            // Add base layer
            const baseLayer = L.tileLayer(MAP_URLS.ROAD_MAP, {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                maxZoom: 25
            }).addTo(map);

            mapInstanceRef.current = map;
            baseLayerRef.current = baseLayer;

            // Cleanup function
            return () => {
                if (mapInstanceRef.current) {
                    mapInstanceRef.current.remove();
                    mapInstanceRef.current = null;
                }
                baseLayerRef.current = null;
            };
        }
    }, [center]);

    // Switch between base layers
    const switchBaseLayer = () => {
        if (baseLayerRef.current) {
            const newLayerType = activeBaseLayer === "satelite" ? "terrain" : "satelite";
            setActiveBaseLayer(newLayerType);
            
            const newUrl = newLayerType === "satelite" ? MAP_URLS.SATELITE : MAP_URLS.ROAD_MAP;
            baseLayerRef.current.setUrl(newUrl);
        }
    };

    // Clear existing markers
    const clearExistingMarkers = () => {
        if (clusterRef.current && mapInstanceRef.current) {
            mapInstanceRef.current.removeLayer(clusterRef.current);
            clusterRef.current = null;
        }
    };

    // Draw requests on map
    const drawRequestOnMapFn = (data) => {
        if (!mapInstanceRef.current || !data) return;

        clearExistingMarkers();

        const markerOptions = {
            clickable: true,
            draggable: false,
            icon: markerIcon,
        };

        const cluster = L.markerClusterGroup({
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            iconCreateFunction: (cluster) => {
                const count = cluster.getChildCount();
                return L.divIcon({
                    html: `<div style="background-color:green; color:white;"><span>${count}</span></div>`,
                    className: 'marker-cluster marker-cluster-medium active_cluster',
                    iconSize: L.point(45, 45)
                });
            }
        });

        data.forEach(d => {
            if (!d.field_lat || !d.field_lng) return;

            const center = [d.field_lat, d.field_lng];
            const marker = L.marker(center, markerOptions);

            marker.on('click', () => {
                const currentZoom = Math.min(mapInstanceRef.current.getZoom() + 1, 25);
                mapInstanceRef.current.flyTo(center, currentZoom);
            });

            const tooltipContent = `
                <div style="display:flex; height:150px; min-width:200px;">
                    <div style="padding:10px;">
                        <div>Request No: <span style="font-weight:600;">${d.req_number || "-"}</span></div>
                        <div>Request Status: <span style="font-weight:600;">${d.status || "-"}</span></div>
                        <div>Total Amount: <span style="font-weight:600;">${d.total_amount || "-"}</span></div>
                        <div>Placed Date: <span style="font-weight:600;">${
                            d.date_created ? moment(d.date_created).format('DD MMMM, YYYY') : "-"
                        }</span></div>
                        <div>Completed Date: <span style="font-weight:600;">${
                            d.completed_time ? moment(d.completed_time).format('DD MMMM, YYYY') : "-"
                        }</span></div>
                        <div>User Name: <span style="font-weight:600;">${d.user_name || "-"}</span></div>
                        <div>Dsp Name: <span style="font-weight:600;">${d.dsp_name || "-"}</span></div>
                    </div>
                </div>
            `;

            marker.bindTooltip(tooltipContent, {
                className: 'location-tooltip',
                permanent: false,
            });

            cluster.addLayer(marker);
        });

        mapInstanceRef.current.addLayer(cluster);
        clusterRef.current = cluster;
    };

    // Toggle fullscreen mode
    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            mapRef.current?.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    // Handle fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement);
            if (mapInstanceRef.current) {
                setTimeout(() => {
                    mapInstanceRef.current.invalidateSize();
                }, 0);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Update map when data changes
    useEffect(() => {
        if (mapInstanceRef.current) {
            drawRequestOnMapFn(mapData);
        }
    }, [mapData]);

    return (
        <div 
            ref={mapRef} 
            style={{ 
                width: "100%", 
                height: "100%", 
                position: 'relative', 
                borderRadius: "5px",
                padding: "2px", 
                minHeight: height || "420px" ,
                marginTop:"10px"
            }}
        >
            {/* Base layer switcher */}
            <div 
                style={{ 
                    border: 'solid 1px rgb(102, 102, 102, 0.3)', 
                    height: '80px', 
                    width:"80px",
                    backgroundColor: 'rgba(255, 255, 255, 0.9)', 
                    borderRadius: '8px', 
                    cursor: 'pointer', 
                    zIndex: '999', 
                    position: "absolute", 
                    right: "10px", 
                    bottom: '10px' 
                }}
                onClick={switchBaseLayer}
            >
                <div className={`absolute bottom-0 left-[19px] font-[600] text-[12px]  font-manrope ${activeBaseLayer === "satelite" ? "text-black" : "text-[#FFFFFF]"}`}>
                    <p>{activeBaseLayer === "satelite" ? "Terrain" : "Satellite"}</p>
                </div>
                <img 
                    style={{ width: '100%', height: '100%', borderRadius: '4px',objectFit:"co" }} 
                    src={activeBaseLayer === "satelite" ? roadMapView : sateliteView} 
                    alt={activeBaseLayer === "satelite" ? "Road map view" : "Satellite view"} 
                />
            </div>

            {/* Loading indicator */}
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-[1000]">
                    <LoaderComponent width={"2%"} height={"2%"} />
                </div>
            )}

            {/* Fullscreen toggle */}
            {isExpandable && (
                <div style={{ position: "absolute", top:isFullScreen ?"25px": "15px", right: isFullScreen?"25px":"15px", height: '50px', zIndex: '999', cursor: "pointer" }}>
                    <img 
                        src={isFullScreen ? reSizeIcon : reSizeIcon} 
                        onClick={toggleFullScreen} 
                        alt={isFullScreen ? "Collapse" : "Expand"} 
                        width={isFullScreen ? "40px" : "40px"} 
                        height={"50px"} 
                    />
                </div>
            )}
        </div>
    );
};

export default ViewRequestsOnMap;